import { Box, Button, styled, Typography } from "@mui/material";
import NoFiltersMatchImage from "assets/illustrations/noMatchFiltersImage.svg";
import DishTypeItem from "components/dishTypeItem";
import { SaleWrapper } from "components/dishTypeItem/components/SaleWrapper";
import { TutorialModalContent } from "components/drawerActionBar/components/TutorialModal/TutorialModalContent";
import { Header } from "components/global/components/Header";
import { BasicModal } from "components/global/modals/BasicModal";
import { useFilters } from "hooks/useFilters";
import useMealListEvents from "hooks/useMealListEvents";
import { useAppDispatch, useAppSelector } from "hooks/utilsHooks/storeHooks";
import { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
	resetAppState,
	selectAppState,
	selectHowToPanelVisibility,
	selectOpenedDetailDishTypeId,
	setHowToBannerVisibility,
	setOpenedDetailDishTypeId,
} from "slices/appStateSlice";
import {
	selectActualSpotlightDishTypeIds,
	selectAvailableDishTypes,
	selectDishTypesNotIncludedInFilters,
} from "slices/dishesSlice";
import { selectFridgeLightingMode } from "slices/fridgeLightingSlice";
import { selectHasActiveOrder, selectOrderState } from "slices/transactionSlice";
import { LightingModeType, TransactionState } from "types";
import { AppState, DishItemDisplayType } from "types/mainTypes";
import { scrollIntoView } from "utils/scrollIntoView";
import { DownloadAppBanner } from "../howToBanner/DownloadAppBanner";

/**
 * Represents a component that displays a list of meal types.
 *
 * Lighting concerns (overall fridge mode + per-dish spotlights + filter highlighting) are
 * owned by `useConvergeLighting`, mounted globally via `LightingConverger`. This component
 * is responsible only for rendering the dish list and reacting to the UI lifecycle:
 *  - opening the initial dish type detail when the BE indicates a spotlight on mount,
 *  - scrolling the how-to banner into view and resetting app state when the fridge goes IDLE,
 *  - scrolling the how-to banner into view when an order finishes.
 */
export const MealList = () => {
	const openDetailDishTypeId = useAppSelector(selectOpenedDetailDishTypeId);
	const dispatch = useAppDispatch();
	const appState = useAppSelector(selectAppState);
	const isHowToBannerVisible = useAppSelector(selectHowToPanelVisibility);
	// Subscribe to the primitive projections (not the whole order object, which gets a new reference
	// every subscription frame) so MealList re-renders only when the order's presence/state changes.
	const hasOrder = useAppSelector(selectHasActiveOrder);
	const orderState = useAppSelector(selectOrderState);
	const availableDishTypes = useAppSelector(selectAvailableDishTypes);
	const unavailableDishTypes = useAppSelector(selectDishTypesNotIncludedInFilters);
	const actualSpotlightDishTypeIds = useAppSelector(selectActualSpotlightDishTypeIds);
	const fridgeLightingMode = useAppSelector(selectFridgeLightingMode);

	const { allergens, resetAllergens } = useFilters();
	const howToBannerRef = useRef<HTMLDivElement>(null);
	const { handleOpen, openDishTypeDetail } = useMealListEvents();

	const { t } = useTranslation();

	// Mount-only: if the BE already has a dish spotlit when we mount (and no allergen filter
	// is active), open that dish's detail so the UI reflects the physical fridge state.
	// `actualSpotlightDishTypeIds` and `allergens` are intentionally read once on mount.
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentionally mount-only — opens the initial dish type detail once
	useEffect(function setInitialOpenedDishType() {
		const initialHighlightedDishTypeId = actualSpotlightDishTypeIds?.[0] ?? null;
		if (initialHighlightedDishTypeId !== null && allergens.length === 0) {
			openDishTypeDetail(initialHighlightedDishTypeId);
		}
	}, []);

	useEffect(
		function handleIdleAndFinishedView() {
			if (appState === AppState.IDLE && !hasOrder) {
				scrollIntoView(howToBannerRef);
				dispatch(resetAppState());
				return;
			}
			if (hasOrder && orderState === TransactionState.Finished) {
				scrollIntoView(howToBannerRef);
			}
		},
		[appState, hasOrder, orderState, dispatch],
	);

	// The backend owns idle-dimming; when it dims the fridge to StandBy, close any open dish detail so
	// the screen returns to the welcome view (a dimmed fridge should never sit behind an open detail).
	// Fire only on the EDGE into StandBy (not continuously while StandBy), so opening a detail during the
	// wake round-trip — when actualMode is briefly still StandBy — is not immediately undone. The converger
	// suppresses per-dish spotlight payloads while StandBy, so this close does not wake the fridge back up.
	const prevLightingModeRef = useRef(fridgeLightingMode);
	useEffect(
		function closeDetailWhenFridgeDims() {
			const dimmedNow =
				fridgeLightingMode === LightingModeType.StandBy && prevLightingModeRef.current !== LightingModeType.StandBy;
			prevLightingModeRef.current = fridgeLightingMode;
			if (dimmedNow && openDetailDishTypeId !== null) {
				dispatch(setOpenedDetailDishTypeId(null));
			}
		},
		[fridgeLightingMode, openDetailDishTypeId, dispatch],
	);

	// "No dishes match your filters": an allergen filter is active and it removed every available dish.
	const isFridgeEmpty = useMemo(
		() => availableDishTypes.length === 0 && allergens.length > 0,
		[availableDishTypes, allergens],
	);

	return (
		<>
			<Header />
			{isFridgeEmpty && (
				<StyledEmptyFridgePageContent>
					<StyledEmptyFridgeHeadline variant="promoHeader">
						{t("dishListPage.filteredEmptyState")}
					</StyledEmptyFridgeHeadline>
					<NoFiltersMatchImage />
				</StyledEmptyFridgePageContent>
			)}
			<StyledDishTypeItemsWrapper data-testid="meal-list-dishes">
				<BasicModal
					toggleButton={
						<DownloadAppBanner
							ref={howToBannerRef}
							isVisible={isHowToBannerVisible}
							toggleBanner={() => dispatch(setHowToBannerVisibility(false))}
						/>
					}
					closable={true}
					modalContent={<TutorialModalContent />}
					fullHeight={true}
				/>

				{availableDishTypes.map((dishType) => (
					<SaleWrapper key={`mealListPage_item_${dishType.id}`} dishType={dishType}>
						<DishTypeItem
							dishType={dishType}
							type={DishItemDisplayType.LIST}
							isOpen={openDetailDishTypeId === dishType.id}
							onOpen={handleOpen}
						/>
					</SaleWrapper>
				))}
				{allergens.length > 0 && unavailableDishTypes.length > 0 && (
					<>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
							}}
						>
							<Typography color="white" variant="mealSub">
								{t("filters.dishesNotMatchingFilters")}
							</Typography>
							<Button
								onClick={() => {
									resetAllergens();
								}}
								variant="es-text"
							>
								{t("filters.resetButton")}
							</Button>
						</Box>
						{unavailableDishTypes.map((dishType) => (
							<SaleWrapper key={dishType.id} dishType={dishType}>
								<DishTypeItem
									dishType={dishType}
									type={DishItemDisplayType.LIST}
									isOpen={openDetailDishTypeId === dishType.id}
									onOpen={handleOpen}
									notFiltered={true}
								/>
							</SaleWrapper>
						))}
					</>
				)}
			</StyledDishTypeItemsWrapper>
		</>
	);
};

const StyledDishTypeItemsWrapper = styled(Box)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	gap: theme.spacing(2),
	overflow: "auto",
	height: "calc(100vh - 350px)",
	paddingBottom: "200px",
}));

const StyledEmptyFridgePageContent = styled(Box)(({ theme }) => ({
	width: "100%",
	height: "100%",
	margin: "auto",
	display: "flex",
	flexDirection: "column",
	alignItems: "center",
	paddingTop: theme.spacing(2.5),
}));

const StyledEmptyFridgeHeadline = styled(Typography)(({ theme }) => ({
	textAlign: "center",
	color: theme.palette.copper.main,
	marginBottom: theme.spacing(3),
}));
